import { DeleteOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons';
import { Button, Image, Modal, Pagination, Popconfirm, Progress, Tag, Typography } from 'antd';
import SpinLoader from 'components/Loader/SpinLoader';
import moment from 'moment-timezone';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FileUploader } from 'react-drag-drop-files';
import { useDispatch } from 'react-redux';
import RenderIfVisible from 'react-render-if-visible';
import { useImageGalleryForAdmin } from 'store/image_gallery/selectors';
import {
  handleCreateGalleryImage,
  handleDeleteGalleryImage,
  handleListGalleryImage,
} from 'store/image_gallery/thunks';
import styled from 'styled-components';
import { imageUploader } from 'utils/methods/imageUploader';
import { getImageThumbPrimaryUrl } from 'utils/methods/storageImageThumbSrcList';
import { useAlert } from '../alertSakeBar/alertProvider';
import { UPLOAD_IMAGE_TO_FIREBASE } from "utils/constants/enum"

const { Title } = Typography;

const ImageGridWrapper = styled.div`
  margin-bottom: 40px;
  padding: 24px;
  background: #f9f9f9;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
`;

const DateLabel = styled(Tag)`
  font-size: 16px;
  font-weight: bold;
  padding: 6px 16px;
  margin-bottom: 12px;
  display: inline-block;
  background-color: #e6f7ff;
  color: #0050b3;
`;

const ImageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 12px;
`;

const ImageItem = styled.div`
  cursor: pointer;
  text-align: center;
  position: relative;

  img {
    width: 100%;
    height: 100px;
    object-fit: cover;
    border-radius: 4px;
  }

  .delete-icon {
    position: absolute;
    top: 5px;
    right: 5px;
    color: red;
    font-size: 16px;
    cursor: pointer;
    background: white;
    border-radius: 50%;
    padding: 2px;
  }
`;

const MultiFileUploader = ({ inModal = false, onSelect, buttonStyle = {}, buttonClass }) => {
  const showAlert = useAlert();
  const dispatch = useDispatch();
  const image_gallery = useImageGalleryForAdmin();
  const [uploadedImages, setUploadedImages] = useState([]);
  const [groupedImages, setGroupedImages] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [modalVisible, setModalVisible] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [loader, setLoader] = useState(false);
  const [totalImageCount, setTotalImageCount] = useState(0);

  const pageSize = 100;

  const getThumbnail = useCallback((url) => getImageThumbPrimaryUrl(url ?? ''), []);

  const groupImagesByDate = (images) => {
    const grouped = images.reduce((acc, img) => {
      if (!img?.create_on) return acc;
      const date = moment(img.create_on).format('YYYY-MM-DD');
      acc[date] = acc[date] || [];
      acc[date].push(img);
      return acc;
    }, {});
    setGroupedImages(grouped);
  };

  const fetchImages = async (offset = 0, limit = pageSize) => {
    setLoader(true);
    try {
      await dispatch(handleListGalleryImage(offset, limit));
    } catch (err) {
      console.error('Error fetching images:', err);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    if (!image_gallery?.data?.length) {
      fetchImages();
    }
  }, []);

  useEffect(() => {
    if (Array.isArray(image_gallery?.data)) {
      const data = image_gallery.data;
      setUploadedImages(data);
      groupImagesByDate(data);
      setTotalImageCount(image_gallery.total || 0);
    }
  }, [image_gallery]);

  const handleUpload = async (files) => {
    const fileArray = Array.from(files).filter(file =>
      file?.type?.startsWith('image/')
    );

    setLoader(true);
    const results = await Promise.allSettled(
      fileArray.map(async (file, idx) => {
        const onProgress = (percent) => {
          setUploadProgress((prev) => ({ ...prev, [idx]: percent }));
        };
        const url = await imageUploader(file, onProgress, UPLOAD_IMAGE_TO_FIREBASE);
        const { data } = await dispatch(handleCreateGalleryImage(url));
        setUploadProgress((prev) => {
          const newProgress = { ...prev };
          delete newProgress[idx];
          return newProgress;
        });
        return data;
      })
    );

    const successfulUploads = results
      .filter((r) => r.status === 'fulfilled')
      .map((r) => r.value);

    const unique = Array.from(new Map([...successfulUploads, ...uploadedImages].map(img => [img.id, img])).values());

    setUploadedImages(unique);
    groupImagesByDate(unique);
    setLoader(false);
    showAlert('Images uploaded successfully', 'success');
  };

  const handleDelete = async (id) => {
    try {
      await dispatch(handleDeleteGalleryImage(id));
      showAlert('Image deleted', 'success');
      setUploadedImages(prev => prev.filter(img => img.id !== id));
      fetchImages((currentPage - 1) * pageSize, pageSize);
    } catch (err) {
      showAlert('Failed to delete image', 'error');
    }
  };

  const renderImages = useMemo(() => {
    const sortedDates = Object.keys(groupedImages).sort((a, b) => new Date(b) - new Date(a));
    return sortedDates.map((date) => (
      <ImageGridWrapper key={date}>
        <DateLabel>{moment(date).format('dddd, MMMM D, YYYY')}</DateLabel>
        <ImageGrid>
          {groupedImages[date].map((img, idx) => (
            <RenderIfVisible
              key={img.id}
              defaultHeight={100}
              visibleOffset={500}
              initialVisible={idx < 20}
              stayRendered
            >
              <ImageItem>
                <Image
                  src={getThumbnail(img.url)}
                  alt={`img-${img.id}`}
                  preview={inModal?false:{ src: img.url }}
                  onClick={() => {
                    if (inModal && onSelect) {
                      onSelect(img.url);
                      setModalVisible(false);
                    }
                  }}
                />
                <Popconfirm
                  title="Delete this image?"
                  onConfirm={() => handleDelete(img.id)}
                  okText="Yes"
                  cancelText="No"
                  zIndex={99999}
                >
                  <DeleteOutlined className="delete-icon" />
                </Popconfirm>
              </ImageItem>
            </RenderIfVisible>
          ))}
        </ImageGrid>
      </ImageGridWrapper>
    ));
  }, [groupedImages, getThumbnail, inModal, onSelect]);

  const Content = (
    <>
      <SpinLoader dataloader={loader} />
      <FileUploader handleChange={handleUpload} name="file" multiple types={['JPG', 'PNG', 'JPEG', 'GIF']}>
        <div style={{
          border: '2px dashed #d9d9d9',
          padding: '40px',
          textAlign: 'center',
          borderRadius: '4px',
          marginBottom: '20px',
          marginTop: '5px'
        }}>
          <p><UploadOutlined /> Click or drag multiple images to upload</p>
        </div>
      </FileUploader>

      {!!Object.keys(uploadProgress).length && (
        <div style={{ padding: '10px 0' }}>
          {Object.entries(uploadProgress).map(([uid, percent]) =>
            Math.round(percent) < 100 ? (
              <Progress key={uid} percent={Math.round(percent)} size="small" />
            ) : null
          )}
        </div>
      )}

      {renderImages}

      <Pagination
        current={currentPage}
        total={totalImageCount}
        pageSize={pageSize}
        onChange={(page) => {
          setCurrentPage(page);
          fetchImages((page - 1) * pageSize, pageSize);
        }}
        style={{ marginTop: 20, textAlign: 'center' }}
      />
    </>
  );

  return inModal ? (
    <>
      <Modal
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={800}
        zIndex={99999}
        className="multiFileUploaderModal"
      >
        {Content}
      </Modal>
      <Button
        className="selectimgGalery"
        icon={<PlusOutlined />}
        // className={buttonClass}
        onClick={() => setModalVisible(true)}
        style={{marginTop: 5, marginBottom: 16, ...buttonStyle }}
      >
        Select Image from Gallery
      </Button>
    </>
  ) : Content;
};

export default MultiFileUploader;